import { useMemo } from 'react';

import { selectRoomDbData } from 'client/redux/room/selectors';
import { selectIsLoggedIn, selectUserRoomRole, selectUserName } from 'client/redux/client/selectors';
import { getRoomHost } from 'client/utils/filter-users-list';
import { USER_ROLES } from 'constants/index';

import { useAppSelector } from './redux';

enum GA_USERS {
  GUEST = 'guest',
}

type Result = {
  common: {
    room_id?: string;
    chat_title?: string;
    chat_subtitle?: string;
    host_name?: string;
    logged_user?: number;
    user_nickname?: string;
    user_type?: USER_ROLES | GA_USERS;
    regular_chat?: number;
    recorded_chat?: number;
    timelimited_chat?: number;
  };
};

export function useGAEventCommonProps(): Result {
  const roomData = useAppSelector(selectRoomDbData);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const userName = useAppSelector(selectUserName);
  const userRole = useAppSelector((state) => selectUserRoomRole(state, roomData?._id));
  const host = roomData ? getRoomHost(roomData._id, roomData.speakers) : undefined;
  const defaultParams = {
    user_joined: undefined,
    user_left: undefined,
    share: undefined,
    emotion: undefined,
    ask_premission: undefined,
    start_chat_timestamp: undefined,
    end_chat_timestamp: undefined,
    chat_time_interval: undefined,
    text_clicked: undefined,
  };

  const common = useMemo(() => {
    if (!roomData) return {};

    // params with undefined values - DYNEMIC default params (from all ga-events);
    // added here to prevent merging events params in GA
    return {
      room_id: roomData._id,
      chat_title: roomData.title,
      chat_subtitle: roomData.description,
      host_name: host?.name ?? '-',
      logged_user: isLoggedIn ? 1 : 0,
      user_nickname: userName ?? '-',
      user_type: isLoggedIn ? userRole : GA_USERS.GUEST,
      regular_chat: 1,
      recorded_chat: roomData.needToRecord ? 1 : 0,
      timelimited_chat: 1,
      ...defaultParams,
    };
  }, [roomData, userName, userRole, isLoggedIn, host]);

  return { common };
}
